<template>
  <section class="container mx-auto px-4 md:px-8 py-8">
    <div
      class="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
    >
      <h1 class="text-2xl md:text-3xl font-bold">Каталог</h1>
    </div>

    <!-- керування фільтрами -->
    <div class="mb-2 flex items-center justify-between gap-3 min-h-10">
      <button
        class="inline-flex items-center gap-2 px-3 h-10 rounded-lg border hover:bg-gray-50 text-sm max-w-full sm:max-w-[520px] min-w-0 overflow-hidden"
        @click="showFilters = !showFilters"
      >
        <span class="shrink-0">Категорії</span>
        <span v-if="activeCatLabel" class="truncate text-gray-600"
          >: {{ activeCatLabel }}</span
        >
        <ChevronDown
          class="w-4 h-4 ml-auto transition-transform shrink-0"
          :class="showFilters ? 'rotate-180' : ''"
        />
      </button>

      <select
        v-model="sort"
        class="border rounded-lg px-3 h-10 outline-none text-sm focus:ring-2 focus:ring-pink-200 bg-white"
      >
        <option value="new">Спочатку нові</option>
        <option value="price-asc">Ціна: зростання</option>
        <option value="price-desc">Ціна: спадання</option>
      </select>
    </div>

    <transition name="collapse">
      <div v-show="showFilters" class="mt-3 mb-3 flex flex-wrap gap-2 text-sm">
        <button
          class="px-3 py-1.5 rounded-full border transition"
          :class="
            filters.cats.length === 0
              ? 'bg-pink-200 text-black border-pink-200'
              : 'hover:bg-gray-50'
          "
          @click="clearCategories"
        >
          Усі
        </button>

        <button
          v-for="c in cats"
          :key="c.id"
          class="px-3 py-1.5 rounded-full border transition"
          :class="
            filters.cats.includes(c.slug)
              ? 'bg-pink-200 text-black border-pink-200'
              : 'hover:bg-gray-50'
          "
          @click="toggleCategory(c.slug)"
        >
          {{ c.label }}
        </button>

        <button
          class="px-3 py-1.5 rounded-full border transition"
          :class="
            exclusive
              ? 'bg-pink-200 text-black border-pink-200'
              : 'hover:bg-gray-50'
          "
          @click="exclusive = !exclusive"
        >
          Ексклюзив
        </button>
        <button
          v-if="filters.cats.length"
          class="px-3 py-1.5 rounded-full border hover:bg-gray-50"
          title="Скинути фільтр"
          @click="clearCategories"
        >
          Скинути
        </button>
      </div>
    </transition>

    <!-- Помилка завантаження. Порожній каталог і збій — РІЗНІ стани:
         при збої сервер віддає 503 (щоб SWR не запік порожню сторінку),
         а клієнт мовчки пробує ще раз. -->
    <div v-if="failed" class="mb-4 text-sm text-red-600 max-w-xl">
      Не вдалося завантажити товари. Пробуємо ще раз…
    </div>

    <!-- Скелетон на БУДЬ-ЯКИЙ перезбір першої сторінки, а не лише коли
         показувати нічого. Раніше тут стояв напівпрозорий оверлей поверх
         старої сітки (успадковано від softLoading до 9а) — і при зміні
         фільтра користувач дивився на приглушені картки ІНШОЇ категорії,
         тобто явно неправильні. Скелетон чесніший: він не показує даних,
         яких уже немає.

         «Показати ще» сюди не потрапляє — у нього окремий стан
         loadingMore, сітка при догрузці лишається на місці.

         На першому рендері скелетона не буде: SSR приносить сторінку
         готовою, pending одразу false. -->
    <CatalogProductGrid v-if="showSkeleton" loading density="compact" />

    <div v-else class="relative">
      <CatalogProductGrid
        :items="items"
        density="compact"
        empty-text="Поки немає товарів."
      />

      <div v-if="hasMore" class="flex justify-center mt-8">
        <button
          class="px-6 py-3 rounded-xl font-semibold transition bg-pink-200 text-black hover:bg-pink-200/80 disabled:opacity-50"
          :disabled="loadingMore"
          @click="fetchMore()"
        >
          {{ loadingMore ? "Завантажуємо…" : "Показати ще" }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
/* ====================================================================
 *  Сторінка каталогу. REFACTOR_PLAN п.9а.
 *
 *  До 9а товари вантажились у onMounted прямим Firestore-запитом із
 *  браузера. Оскільки в routeRules стоїть `swr: 60`, у кеш лягала
 *  ПОРОЖНЯ оболонка: заміряно на проді — 0 товарів і 0 JSON-LD у сирому
 *  HTML. Тепер перша сторінка приходить із /api/catalog на сервері.
 *
 *  Сторінка навмисно НЕ знає ні про Firestore, ні про сортування: перше
 *  живе в ендпоінті, друге — у pure-модулі catalogFilter, спільному для
 *  сервера й клієнта.
 * ==================================================================== */
import { computed, ref, watch, onMounted } from "vue";
import {
  useAsyncData,
  useHead,
  useRequestEvent,
  useRuntimeConfig,
  useSeoMeta,
  setResponseStatus,
} from "#imports";
import { ChevronDown } from "lucide-vue-next";
import { useCategoriesData } from "~/composables/useCategoriesData";
import { useTracking, buildGaListItems } from "~/composables/useTracking";
import { useCatalogQuery } from "~/composables/catalog/useCatalogQuery";
import {
  CATALOG_COMPLETE_LIMIT,
  CATALOG_PAGE_SIZE,
  selectCatalogPage,
  toCatalogQuery,
} from "~/composables/catalog/catalogFilter";
import type { CatalogPage } from "~/composables/catalog/catalogFilter";
import type { GridItem } from "~/composables/catalog/gridItems";

const { cats } = useCategoriesData();
const { filters, sort, exclusive, toggleCategory, clearCategories } =
  useCatalogQuery();

const showFilters = ref(false);

const activeCatLabel = computed(() => {
  if (!filters.value.cats.length) return "";
  const labels = filters.value.cats
    .map((s) => cats.value.find((c) => c.slug === s)?.label)
    .filter((l): l is string => !!l);
  return labels.length <= 3
    ? labels.join(", ")
    : `${labels.slice(0, 3).join(", ")} +${labels.length - 3}`;
});

/* ---- перша сторінка: SSR ---- */
// Стежимо за СЕРІАЛІЗОВАНИМИ фільтрами, а не за об'єктом: `filters` —
// computed, який віддає новий об'єкт на будь-яку зміну query, тож
// стороння utm-мітка інакше спричиняла б зайвий перезапит.
const filterKey = computed(() => JSON.stringify(toCatalogQuery(filters.value)));

// Ключ статичний, перезбір вішається на watch: так payload лишається
// одним записом, а не плодиться по комбінаціях фільтрів.
//
// `lazy: true` — і БЕЗ top-level await. Перша версія 9а блокувала setup,
// через що клік по «Каталог» не давав НІЯКОГО відгуку: Suspense тримав
// стару сторінку, доки не прийдуть дані й не відмалюються 60 карток
// (у дев-режимі це відчувалось як 3-4 с зависання). До 9а перехід був
// миттєвим, бо сторінка монтувалась порожньою і одразу малювала скелетон.
// `lazy` повертає саме це: на клієнтську навігацію не чекаємо, скелетон
// показується одразу. На SSR це не впливає — там Nuxt однаково дочекається
// даних до завершення рендеру, тож сирий HTML лишається повним.
// Поки каталог влазить у стелю, тягнемо його ЦІЛКОМ і БЕЗ фільтрів —
// далі сторінка фільтрує сама, тією ж pure-функцією, що й сервер. Саме
// тому запит навмисно не містить `cat/sort/ex`: фільтрований набір не
// дав би змоги перемкнутись на інший фільтр без нової подорожі в мережу.
// Якщо каталог переросте стелю, `complete` прийде false, і сторінка
// одноразово перемкнеться на серверну фільтрацію (див. нижче).
const serverFiltering = ref(false);

const catalogData = useAsyncData<CatalogPage>(
  "catalog-page",
  () =>
    $fetch("/api/catalog", {
      query: serverFiltering.value
        ? { ...toCatalogQuery(filters.value), limit: CATALOG_PAGE_SIZE }
        : { limit: CATALOG_COMPLETE_LIMIT },
    }),
  { watch: [filterKey], lazy: true },
);
const { data, pending, error, refresh } = catalogData;

const failed = computed(() => !!error.value);

/**
 * Чи можна фільтрувати локально. `complete` означає «у відповіді вся
 * вибірка, нічого не відрізано» — тільки тоді клієнтська фільтрація не
 * бреше. Це запобіжник, а не оптимізація: на обрізаному наборі вона
 * тихо ховала б товари.
 */
const localMode = computed(
  () => !serverFiltering.value && !!data.value?.complete,
);

// Одноразовий перемикач: каталог переріс стелю — далі фільтрує сервер.
watch(data, (page) => {
  if (page && page.complete === false && !serverFiltering.value) {
    serverFiltering.value = true;
    refresh();
  }
});

// На сервері чекаємо явно — і лише тут. Клієнтський бандл цю гілку не
// виконує (import.meta.server згортається у false), тож навігація
// лишається миттєвою, а сирий HTML — повним.
if (import.meta.server) {
  await catalogData;

  // Каталог переріс стелю — на сервері перемикаємось одразу, бо
  // watch нижче на SSR не спрацює, а віддати сирий нефільтрований
  // зріз у HTML означало б показати краулеру не ту сторінку.
  if (data.value?.complete === false) {
    serverFiltering.value = true;
    await refresh();
  }

  // Збій даних мусить дати НЕ-200: Nitro не кешує лише відповіді >= 400,
  // тож порожня, але успішна сторінка запеклася б у SWR-кеш на хвилину.
  if (error.value) {
    const event = useRequestEvent();
    if (event) setResponseStatus(event, 503);
  }
}

/* ---- «Показати ще»: догружені сторінки ---- */
const more = ref<{ items: GridItem[]; hasMore: boolean | null }>({
  items: [],
  hasMore: null,
});
const loadingMore = ref(false);

/** Скільки карток показано в локальному режимі. Росте кнопкою. */
const shown = ref(CATALOG_PAGE_SIZE);

// Зміна фільтрів скидає догружене — інакше картки з попередньої вибірки
// лишились би під новою.
watch(filterKey, () => {
  more.value = { items: [], hasMore: null };
  shown.value = CATALOG_PAGE_SIZE;
});

/**
 * Видима сторінка. У локальному режимі рахується тут-таки з повного
 * набору — та сама `selectCatalogPage`, що працює на сервері, тож
 * результат за побудовою збігається з серверним на тих самих даних.
 */
const localPage = computed<CatalogPage | null>(() =>
  localMode.value && data.value
    ? selectCatalogPage(data.value.items, {
        ...filters.value,
        limit: shown.value,
      })
    : null,
);

const items = computed<GridItem[]>(() =>
  localPage.value
    ? localPage.value.items
    : [...(data.value?.items ?? []), ...more.value.items],
);

const hasMore = computed(() =>
  localPage.value
    ? localPage.value.hasMore
    : (more.value.hasMore ?? data.value?.hasMore ?? false),
);

/**
 * Скелетон — лише коли показати правильне нічим. У локальному режимі
 * дані відповідають будь-якому фільтру одразу, тож перезбір (він
 * лишається як фонове оновлення свіжості) не має блимати скелетоном.
 */
const showSkeleton = computed(() => pending.value && !localMode.value);

async function fetchMore() {
  if (loadingMore.value || !hasMore.value) return;

  // Локальний режим: нічого не вантажимо, просто показуємо більше.
  if (localMode.value) {
    shown.value += CATALOG_PAGE_SIZE;
    emitViewItemList(items.value.slice(-CATALOG_PAGE_SIZE), "load_more");
    return;
  }

  loadingMore.value = true;
  try {
    // offset — довжина вже показаного. Працює саме тому, що сервер
    // сортує ПОВНУ вибірку: порядок наскрізний, і сторінки не
    // перекриваються. До 9а кожна догружена порція сортувалась окремо.
    const res = await $fetch<CatalogPage>("/api/catalog", {
      query: {
        ...toCatalogQuery(filters.value),
        offset: items.value.length,
        limit: CATALOG_PAGE_SIZE,
      },
    });
    more.value = {
      items: [...more.value.items, ...res.items],
      hasMore: res.hasMore,
    };
    emitViewItemList(res.items, "load_more");
  } finally {
    loadingMore.value = false;
  }
}

/* ---- GA4 ---- */
const LIST_NAME = "catalog";
const { ga: gaLog } = useTracking();
const route = useRoute();

function emitViewItemList(snapshot: GridItem[], tag = "") {
  if (!import.meta.client || !snapshot?.length) return;
  gaLog("view_item_list", {
    item_list_name: LIST_NAME,
    item_list_id: route.fullPath,
    items: buildGaListItems(snapshot, {
      name: LIST_NAME,
      id: route.fullPath,
    }),
    list_tag: tag || undefined,
  });
}

onMounted(() => {
  // Дані вже прийшли з сервера — подія шлеться на монтуванні, а не
  // після фетчу.
  emitViewItemList(data.value?.items ?? [], "first_page");
  // Якщо SSR не дістав даних (503) — тиха повторна спроба з клієнта.
  if (error.value) refresh();
});

watch(
  () => data.value,
  (page) => {
    if (import.meta.client) emitViewItemList(page?.items ?? [], "refetch");
  },
);

/* ====================================================================
 *  SEO. До 9а сторінка не мала ні свого title, ні description, ні
 *  структурованих даних: у HTML їхало загальносайтове
 *  «mulberry'ss — інтернет-магазин», а JSON-LD не було зовсім — при
 *  тому що /catalog лежить у sitemap із priority 0.85.
 *
 *  Теги статичні й НЕ залежать від фільтрів: canonical у layout веде на
 *  /catalog без query, тож усі `?cat=…` зводяться до однієї сторінки, і
 *  різні title на них лише плодили б суперечливі сигнали.
 * ==================================================================== */
const TITLE = "Каталог шовкових виробів — наволочки, тюрбани, аксесуари";
const DESCRIPTION =
  "Каталог mulberry'ss: шовкові наволочки, тюрбани, маски для сну, гумки для волосся та рушники з натурального шовку Mulberry. Виготовлено в Україні, доставка Новою Поштою.";

const siteUrl = String(
  useRuntimeConfig().public?.siteUrl || "https://mulberryss.shop",
).replace(/\/$/, "");

useSeoMeta({
  title: TITLE,
  description: DESCRIPTION,
  ogTitle: TITLE,
  ogDescription: DESCRIPTION,
  ogType: "website",
  ogUrl: `${siteUrl}/catalog`,
  twitterCard: "summary_large_image",
  twitterTitle: TITLE,
  twitterDescription: DESCRIPTION,
});

// ItemList будується по ТОВАРАХ, а не по картках: одна модель дає до
// десятка карток-варіантів, і десять ListItem з однаковим URL були б
// сміттям у розмітці.
const itemListLd = computed(() => {
  const seen = new Set<string>();
  const entries: Array<{ url: string; name: string }> = [];

  for (const it of items.value) {
    const p = it.product as { id?: string; slug?: string; title?: string };
    const key = String(p.slug || p.id || "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    entries.push({ url: `${siteUrl}/product/${key}`, name: p.title || "" });
    if (entries.length >= 30) break;
  }

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Каталог mulberry'ss",
    numberOfItems: entries.length,
    itemListElement: entries.map((e, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: e.url,
      name: e.name,
    })),
  };
});

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Головна", item: `${siteUrl}/` },
    {
      "@type": "ListItem",
      position: 2,
      name: "Каталог",
      item: `${siteUrl}/catalog`,
    },
  ],
};

useHead(() => ({
  script: [
    {
      key: "ld-breadcrumb",
      type: "application/ld+json",
      innerHTML: JSON.stringify(breadcrumbLd),
    },
    {
      key: "ld-itemlist",
      type: "application/ld+json",
      innerHTML: JSON.stringify(itemListLd.value),
    },
  ],
}));
</script>

<style scoped>
.collapse-enter-active,
.collapse-leave-active {
  transition:
    max-height 0.25s ease,
    opacity 0.2s ease;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  max-height: 400px;
  opacity: 1;
}
</style>
